import { FileTextIcon, SearchIcon } from '@/components/ui/icons';
import { useTranslation } from 'react-i18next';

interface BlogHeroProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  articlesCount: number;
}

export function BlogHero({ searchTerm, onSearchChange, articlesCount }: BlogHeroProps) {
  const { t } = useTranslation('blog');
  return (
    <section className="bg-gradient-to-br from-aspexit-green to-aspexit-green-dark py-12">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left: Title & Search */}
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              <span className="text-white">{t('hero.title')}</span>
              <br />
              <span className="text-white">{t('hero.subtitle')}</span>
            </h1>
            <p className="text-base md:text-lg text-white/90 mb-6">
              {t('hero.description', { count: articlesCount })}
            </p>

            {/* Search Bar */}
            <div className="relative max-w-lg">
              <SearchIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('hero.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border-0 bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-aspexit-green"
              />
            </div>
          </div>

          {/* Right: Themes Cards */}
          <div className="grid grid-cols-2 gap-3 lg:ml-auto">
            {/* Card 1: Expertise indépendante */}
            <div className="bg-white rounded-xl p-4 border border-border shadow-lg flex flex-col items-center justify-center gap-2 min-h-[100px]">
              <svg viewBox="0 0 100 100" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
                {/* Shield with checkmark */}
                <path d="M 50 10 L 75 20 L 75 45 Q 75 70 50 85 Q 25 70 25 45 L 25 20 Z"
                      fill="#55BC8C" opacity="0.1" />
                <path d="M 50 10 L 75 20 L 75 45 Q 75 70 50 85 Q 25 70 25 45 L 25 20 Z"
                      fill="none" stroke="#55BC8C" strokeWidth="2.5" strokeLinejoin="round" />
                <path d="M 38 48 L 46 56 L 62 40"
                      fill="none" stroke="#55BC8C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="text-sm text-aspexit-black font-medium text-center leading-tight">
                {t('hero.themes.independence')}
              </div>
            </div>

            {/* Card 2: Analyse techno-critique */}
            <div className="bg-white rounded-xl p-4 border border-border shadow-lg flex flex-col items-center justify-center gap-2 min-h-[100px]">
              <svg viewBox="0 0 100 100" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
                {/* Magnifying glass with circuit */}
                <circle cx="40" cy="40" r="20" fill="#55BC8C" opacity="0.1" />
                <circle cx="40" cy="40" r="20" fill="none" stroke="#55BC8C" strokeWidth="2.5" />
                <line x1="54" y1="54" x2="70" y2="70" stroke="#55BC8C" strokeWidth="3" strokeLinecap="round" />
                {/* Circuit inside */}
                <circle cx="35" cy="35" r="2.5" fill="#55BC8C" />
                <circle cx="45" cy="35" r="2.5" fill="#55BC8C" />
                <circle cx="35" cy="45" r="2.5" fill="#55BC8C" />
                <circle cx="45" cy="45" r="2.5" fill="#55BC8C" />
                <line x1="35" y1="35" x2="45" y2="35" stroke="#55BC8C" strokeWidth="1.5" />
                <line x1="35" y1="45" x2="45" y2="45" stroke="#55BC8C" strokeWidth="1.5" />
                <line x1="35" y1="35" x2="35" y2="45" stroke="#55BC8C" strokeWidth="1.5" />
                <line x1="45" y1="35" x2="45" y2="45" stroke="#55BC8C" strokeWidth="1.5" />
              </svg>
              <div className="text-sm text-aspexit-black font-medium text-center leading-tight">
                {t('hero.themes.analysis')}
              </div>
            </div>

            {/* Card 3: Entretiens et Lectures Techniques */}
            <div className="bg-white rounded-xl p-4 border border-border shadow-lg flex flex-col items-center justify-center gap-2 min-h-[100px]">
              <svg viewBox="0 0 100 100" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
                {/* Book with microphone */}
                <rect x="25" y="35" width="35" height="45" fill="#55BC8C" opacity="0.1" rx="2" />
                <rect x="25" y="35" width="35" height="45" fill="none" stroke="#55BC8C" strokeWidth="2.5" rx="2" />
                <line x1="30" y1="45" x2="50" y2="45" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="30" y1="52" x2="55" y2="52" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="30" y1="59" x2="48" y2="59" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
                {/* Microphone */}
                <rect x="60" y="25" width="12" height="20" fill="none" stroke="#55BC8C" strokeWidth="2.5" rx="6" />
                <line x1="66" y1="45" x2="66" y2="55" stroke="#55BC8C" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="58" y1="55" x2="74" y2="55" stroke="#55BC8C" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <div className="text-xs text-aspexit-black font-medium text-center leading-tight">
                {t('hero.themes.interviews')}
              </div>
            </div>

            {/* Card 4: Agronomie, Numérique, Energie, Climat */}
            <div className="bg-white rounded-xl p-4 border border-border shadow-lg flex flex-col items-center justify-center gap-2 min-h-[100px]">
              <svg viewBox="0 0 100 100" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
                {/* 4 themed icons in a grid */}
                {/* Plant (Agronomie) */}
                <path d="M 25 20 Q 25 10 30 10 Q 35 10 35 20" fill="none" stroke="#55BC8C" strokeWidth="2" />
                <line x1="30" y1="15" x2="30" y2="30" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round" />
                {/* Circuit (Numérique) */}
                <rect x="60" y="10" width="15" height="15" fill="none" stroke="#55BC8C" strokeWidth="2" rx="2" />
                <circle cx="63" cy="13" r="1.5" fill="#55BC8C" />
                <circle cx="72" cy="13" r="1.5" fill="#55BC8C" />
                <circle cx="63" cy="22" r="1.5" fill="#55BC8C" />
                <circle cx="72" cy="22" r="1.5" fill="#55BC8C" />
                {/* Lightning (Energie) */}
                <path d="M 30 60 L 25 70 L 30 70 L 28 80 L 35 68 L 30 68 Z" fill="#55BC8C" />
                {/* Sun/Cloud (Climat) */}
                <circle cx="67" cy="72" r="6" fill="none" stroke="#55BC8C" strokeWidth="2" />
                <line x1="67" y1="64" x2="67" y2="62" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="67" y1="82" x2="67" y2="80" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="59" y1="72" x2="61" y2="72" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="75" y1="72" x2="73" y2="72" stroke="#55BC8C" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <div className="text-xs text-aspexit-black font-medium text-center leading-tight">
                {t('hero.themes.topics')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
